import {
  QuestionType,
  QuestionInputType,
  DenormalizedCustomizableQuestion,
  CustomizerProductSubmitAction,
} from '@packages/types'
import classNames from 'classnames'
import { isFinite } from 'lodash'
import React from 'react'

import * as customizationSelectors from 'customizer/customization/selectors'
import { useSelector } from 'customizer/hooks'
import * as pricingSelectors from 'customizer/pricing/selectors'
import * as stocksSelectors from 'customizer/stocks/selectors'
import { RootState } from 'customizer/store'
import useThemeSettings from 'customizer/theme/useThemeSettings'
import Price from 'themes/common/components/Price'
import MultiAnswerSelectionsMessage from 'themes/common/components/questions/MultiAnswerSelectionsMessage'
import ResolutionIndicator from 'themes/common/components/ResolutionIndicator'
import Translation from 'themes/common/components/Translation'

import { invalidStepsSelector } from '../selectors'

const { currencySelector, customizationQuestionPriceSelector } = pricingSelectors

import './Summary.scss'

const requiredInputTypes = [
  QuestionInputType.Text,
  QuestionInputType.File,
  QuestionInputType.Dropdown,
  QuestionInputType.Checkbox,
  QuestionInputType.ColorPicker,
]

const inputTypesWithoutSummary = [
  QuestionInputType.Text,
  QuestionInputType.File,
  QuestionInputType.Dropdown,
  QuestionInputType.ColorPicker,
]

interface SummaryProps {
  question: DenormalizedCustomizableQuestion
  style: React.CSSProperties
  showResolutionIndicator?: boolean
}

const Summary = ({ question, style, showResolutionIndicator = false }: SummaryProps) => {
  const priceStyle = useThemeSettings('price')
  const questionPanelThemeSettings = useThemeSettings('questionPanel')
  const invalidQuestions: string[] = useSelector(invalidStepsSelector)
  const currency = useSelector(currencySelector)
  const lang = useSelector(state => state.lang)
  const country = useSelector(state => state.country)
  const pricing: Record<string, string> = useSelector(state =>
    (customizationQuestionPriceSelector as any)(state, question)
  )
  const submitAction = useSelector(customizationSelectors.submitActionSelector)

  const selectedAnswerIds: string[] = useSelector(state =>
    customizationSelectors.selectedAnswerIdsSelector(state, [question.id])
  )
  const stocksWithAnswersOverAvailable = useSelector(stocksSelectors.stocksWithAnswersOverAvailableSelector)
  const questionOverAvailableStocks = stocksWithAnswersOverAvailable.find(
    stock =>
      !!stock.answersRefs.find(
        answersRef =>
          question.selectedAnswer?.id === answersRef.answerId ||
          question.selectedAnswers?.map(({ id }) => id).includes(answersRef.answerId)
      )
  )

  const answersOutOfStock = useSelector((state: RootState) =>
    stocksSelectors.answersOutOfStockForQuestionsSelector(state, [question.id])
  )

  const isOutOfStock = selectedAnswerIds.some(answerId => answersOutOfStock.includes(answerId))

  const hasRequiredMessage =
    requiredInputTypes.includes(question.inputType) && (question.isRequired || question.isInteractionRequired)

  const isRequiredEmphasized = !!invalidQuestions.find(questionId => questionId === question.id)

  const hasMinSelections = isFinite(question.minSelections) && question.minSelections! > 0
  const hasMaxSelections = isFinite(question.maxSelections) && question.maxSelections! < question.answers.length
  const hasMinOrMax = question.isMultiAnswer && (hasMinSelections || hasMaxSelections)

  const showSummary =
    !inputTypesWithoutSummary.includes(question.inputType) &&
    !hasRequiredMessage &&
    !question.hasFilteredWord &&
    !hasMinOrMax &&
    !isOutOfStock &&
    !questionOverAvailableStocks

  let price

  if (question.isMultiAnswer) {
    price = question.selectedAnswers?.reduce((acc, answer) => {
      const extraPrice = pricing[answer.id]
      return !!extraPrice ? parseFloat(extraPrice) + acc : acc
    }, 0)
  } else if (question.selectedAnswer?.id) {
    price = pricing[question.selectedAnswer.id]
  }

  const getSummary = (question: DenormalizedCustomizableQuestion) => {
    if (question.isMultiAnswer && question.selectedAnswers) {
      return question.selectedAnswers.map(({ name }) => name).join(', ')
    } else if (question.selectedAnswer) {
      return question.selectedAnswer.name
    }

    return ''
  }

  return (
    <div
      className={classNames('summary', {
        'summary--emphasized': isRequiredEmphasized || question.hasFilteredWord,
      })}
      style={style}
    >
      {!isOutOfStock && hasRequiredMessage && !question.hasFilteredWord && (
        <Translation
          field="requiredWarning"
          style={isRequiredEmphasized ? { color: questionPanelThemeSettings.errorColor } : {}}
        />
      )}
      {question.hasFilteredWord && (
        <Translation field="wordFilter" style={{ color: questionPanelThemeSettings.errorColor }} />
      )}

      {showResolutionIndicator && question.type === QuestionType.Logo && (
        <ResolutionIndicator logoQuestion={question} full={false} />
      )}

      {hasMinOrMax && !isOutOfStock && !questionOverAvailableStocks && (
        <MultiAnswerSelectionsMessage
          question={question}
          style={isRequiredEmphasized ? { color: questionPanelThemeSettings.errorColor } : {}}
        />
      )}

      {!question.hasFilteredWord && isOutOfStock && (
        <Translation
          field="outOfStock"
          style={isRequiredEmphasized ? { color: questionPanelThemeSettings.errorColor } : {}}
        />
      )}

      {!question.hasFilteredWord && !!questionOverAvailableStocks && (
        <Translation
          field="availableQuantity"
          values={{ quantity: questionOverAvailableStocks.stock }}
          style={isRequiredEmphasized ? { color: questionPanelThemeSettings.errorColor } : {}}
        />
      )}

      {showSummary && <span className="summary__answer">{getSummary(question)}</span>}

      {!!price && priceStyle.showExtraPrice && submitAction !== CustomizerProductSubmitAction.GetAQuote && (
        <Price currency={currency} amount={price} lang={lang} country={country} className="summary__extra-price" />
      )}
    </div>
  )
}

export default Summary
